/**
 * Data layer for `multi-channel-influencer-campaign-roi-dashboard`. Thin typed fetch wrappers over /api/v1/multi-channel-influencer-campaign-roi-dashboard.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface MultiChannelInfluencerCampaignRoiDashboardRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListMultiChannelInfluencerCampaignRoiDashboardResponse {
	count: number;
	items: MultiChannelInfluencerCampaignRoiDashboardRow[];
}

export interface CreateMultiChannelInfluencerCampaignRoiDashboardBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/multi-channel-influencer-campaign-roi-dashboard";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListMultiChannelInfluencerCampaignRoiDashboardResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListMultiChannelInfluencerCampaignRoiDashboardResponse>(res);
}

export async function create(body: CreateMultiChannelInfluencerCampaignRoiDashboardBody): Promise<MultiChannelInfluencerCampaignRoiDashboardRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<MultiChannelInfluencerCampaignRoiDashboardRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const multiChannelInfluencerCampaignRoiDashboardQueryKeys = {
	all: ["multi-channel-influencer-campaign-roi-dashboard"] as const,
	list: () => [...multiChannelInfluencerCampaignRoiDashboardQueryKeys.all] as const,
	detail: (id: string) => [...multiChannelInfluencerCampaignRoiDashboardQueryKeys.all, id] as const,
};
